// Brief 63 (23 Jun 2026): FE letterhead chrome removed. V's .docx
// templates own the letterhead block (Our Ref, Date, Client name,
// address etc.). Single source of truth = the .docx template body.
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
// Brief 68 (23 Jun 2026): Real templates use <table> for fees + next-steps.
// StarterKit doesn't include Table — TipTap silently drops table content
// without these extensions, so the fees table renders as a stack of paragraphs.
// TipTap v3.27+ uses named exports (no default) for Table; the row/cell/header
// sub-packages still expose defaults, but we import everything by name from
// the parent package for consistency and to dodge the no-default-export issue.
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3,
  Save, CheckCircle2, Loader2, FileDown, Undo2, Redo2, Eye, PenLine, Wand2,
  BetweenHorizontalStart, BetweenHorizontalEnd, Trash2, Table2
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

// Brief 57R (23 Jun 2026): AI Sweep and AI suggest content buttons
// removed. The AI Once-Over is the only AI-flavoured action that
// remains. Vanessa's call: one button only.
// Brief 69.1 (24 Jun 2026): the AI Once-Over trigger + results now live on the
// comments rail. The old CCLAIReview panel below the document is gone; its
// fetch/transform logic moved into the useCclSweep hook.

import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { markdownToHtml } from "../utils/cclMarkdown";
import CclCommentsRail from "./CclCommentsRail";
import CclPostReviewActions from "./CclPostReviewActions";
import CclRevertToDraft from "./CclRevertToDraft";
import UatMatterToggle from "./UatMatterToggle";
import CclSentSummary from "./CclSentSummary";
import CclManualUpload from "./CclManualUpload";
import CclSentOffSystem from "./CclSentOffSystem";
import { parseBlobError } from "../utils/parseBlobError";
import { useCclSweep } from "../hooks/useCclSweep";
import { useCclPresweep, presweepCards } from "../hooks/useCclPresweep";
import CclForfCheck from "./CclForfCheck";
import {
  applySuggestion,
  blocksFromDoc,
  findSuggestionBlock,
  undoChange,
  STALE_MESSAGE,
} from "../utils/cclBlocks";

// ---------------------------------------------------------------------------
// BM-H522: error-detail formatting now goes through the canonical
// errorMessage() helper (utils/errorMessage.js) rather than this file's own
// formatErrorDetail. Same object/string priority chain, firm-wide helper.
// ---------------------------------------------------------------------------
// Brief 42b — comment helpers
// ---------------------------------------------------------------------------

// Merge new comments in, replacing any existing comment with the same id so a
// re-run of the sweep refreshes anchored cards rather than duplicating them.
function mergeComments(existing, incoming) {
  const byId = new Map(existing.map((c) => [c.id, c]));
  incoming.forEach((c) => byId.set(c.id, c));
  return Array.from(byId.values());
}

// CSS.escape shim for selector building (older jsdom lacks it).
function cssEscape(value) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return String(value).replace(/["\\\]]/g, "\\$&");
}

// Tag rendered document blocks with data-anchor-id by matching each swept
// section's original text against the live paragraphs. Best-effort: a section
// whose text can't be located simply stays anchorless (its card still works,
// just without scroll-to). Runs on the A4 scroll container DOM, not TipTap
// state, so it never mutates document content.
function tagAnchors(scrollEl, sections) {
  if (!scrollEl || !Array.isArray(sections)) return;
  const blocks = Array.from(
    scrollEl.querySelectorAll(".ProseMirror p, .ProseMirror h2, .ProseMirror h3, .ProseMirror li"),
  );
  if (blocks.length === 0) return;
  const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();

  sections.forEach((section) => {
    const needle = norm(section.original).slice(0, 40);
    if (!needle) return;
    const match = blocks.find((b) => norm(b.textContent).includes(needle));
    if (match) match.setAttribute("data-anchor-id", section.id);
  });
}

// BM-H596: the place a drafted fix goes, as re-validated by the server.
function placeFrom(drafted, comment) {
  return {
    op: drafted.op || "advisory",
    blockId: drafted.blockId ?? null,
    anchorName: drafted.anchorName || null,
    targetText: drafted.targetText || null,
    blockSha: drafted.blockSha || null,
    blockText: drafted.blockText || null,
    quote: drafted.targetText || drafted.anchorQuote || comment.quote || null,
    stale: false,
  };
}

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------
function ToolbarButton({ onClick, active, disabled, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? "bg-teal/10 text-teal"
          : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CclEditor({
  submissionId,
  submissionData,
  onReviewConfirmed,
  initialReviewed = false,
  matterType = "",
  feeQuoted = "",
  clientName = "",
  // BM-H474 (9 Sep 2026): recorded client-signature state from the server, so
  // the post-review bar knows about a signature given before this page load.
  signingStatus = null,
  signingToken = null,
  // BM-H597 (23 Sep 2026): Revert to draft. carePackSent tells the dialog to
  // warn that the client's signing link will stop working; onReverted lets the
  // matter page drop its reviewed state.
  carePackSent = false,
  onReverted,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [reviewed, setReviewed] = useState(initialReviewed);
  // BM-H597: the live signing state, fed by the post-review bar's polling, and
  // whether this letter has been reverted in this session (after which the old
  // signing token is revoked and must not be polled again).
  const [liveSigningStatus, setLiveSigningStatus] = useState(signingStatus);
  const [revertedHere, setRevertedHere] = useState(false);
  const cclClientSigned = signingStatus === "signed" || liveSigningStatus === "signed";
  const cclSent = !revertedHere && Boolean(carePackSent || signingToken || liveSigningStatus);
  // BM-H3747 (Vanessa, 6 Oct 2026): once the letter has gone to the client
  // the editor and the AI rail fold away behind a short summary.
  const [letterOpen, setLetterOpen] = useState(false);
  // 8 Oct 2026: a letter recorded as sent off CaseMinder folds too. Only
  // CclSentOffSystem loads that record, and it reports it here.
  const [offSystem, setOffSystem] = useState(null);
  const sentAnyWay = cclSent || Boolean(offSystem?.sent_off_system);
  const folded = sentAnyWay && !letterOpen;
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [source, setSource] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [editorHtml, setEditorHtml] = useState("");

  // ── Brief 42b — Word-style anchored comments rail ──────────────────
  // AI suggestions are normalised into a single `comments` model and
  // rendered in a docked rail to the right of the document (never an
  // overlay). The rail is collapsible. Sweep + generative-suggest both
  // feed comments. Anchors tie each comment to a document block via
  // data-anchor-id so card<->paragraph scrolling works both ways.
  const [comments, setComments] = useState([]);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [activeAnchorId, setActiveAnchorId] = useState(null);
  const a4ScrollRef = useRef(null);

  // ── BM-H11 (2 Jul 2026) — structural-error dialog state ────────────
  // 422 from POST /confirm-review carries {code, message, errors:[{code,
  // severity, message, section?}], matter_type, matter_reference}. Toast
  // is too small to show which sections are missing, so we surface the
  // detail in a dedicated dialog with a full list.
  const [structuralError, setStructuralError] = useState(null);

  // BM-H595 (23 Sep 2026): the draft versus the FORF
  // forfChanged: the FORF was edited after this draft was built or saved.
  // forfFacts: the FORF answers the letter is built from (money, scope,
  // names), used by the once-over instead of the old Q10 fee field.
  const [forfChanged, setForfChanged] = useState(false);
  const [forfFacts, setForfFacts] = useState(null);
  const [forfFindings, setForfFindings] = useState([]);
  const [rebuilding, setRebuilding] = useState(false);
  const [undoingAll, setUndoingAll] = useState(false);
  // Filled in once the pre-draft check hook is set up below, so the load
  // effect can hand it the status that came with the letter.
  const seedPresweepRef = useRef(null);
  const fetchLastReviewRef = useRef(null);
  const handleSaveRef = useRef(null);

  const [inTable, setInTable] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      // Brief 68: Tables — fees grid + next-steps table from the .docx templates.
      // resizable=false because Vanessa's templates set column widths intentionally.
      Table.configure({ resizable: false, HTMLAttributes: { class: "ccl-table" } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: "<p>Loading CCL...</p>",
    editable: !reviewed,
    // BM-H4517: the row buttons show only while the cursor is in a table.
    onSelectionUpdate: ({ editor }) => setInTable(editor.isActive("table")),
    onUpdate: ({ editor }) => {
      setInTable(editor.isActive("table"));
      setEditorHtml(editor.getHTML());
      // BM-H11: any edit (typed, AI-inserted, comment-accepted, undo/redo)
      // invalidates the saved snapshot. Without this, hasSaved stays true
      // after AI Once-Over inserts content and Confirm Review posts against
      // stale DB HTML.
      setHasSaved(false);
    },
    editorProps: {
      attributes: {
        // Matches the A4 body area — no extra padding here, handled by the container
        class: "prose prose-sm max-w-none focus:outline-none min-h-[900px]",
      },
    },
  });

  // ------------------------------------------------------------------
  // Load CCL content
  // ------------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/submissions/${submissionId}/ccl-content`);
        const data = res.data;
        if (data.html && editor) {
          editor.commands.setContent(data.html);
          setEditorHtml(data.html);
          setSource(data.source);
          if (data.source === "saved") {
            setHasSaved(true);
            setLastSavedAt(data.saved_at);
          }
          // BM-H595: the FORF check state that comes with the letter.
          setForfChanged(Boolean(data.forf_changed));
          setForfFacts(data.forf_facts || null);
          setForfFindings(data.forf_findings || []);
          seedPresweepRef.current?.(data.presweep);
        }
      } catch (err) {
        console.error("Failed to load CCL:", err);
        // Brief ccl-bridge-r1 §3.4 — surface the 422 detail so the
        // lawyer sees "no matter_type set" / "template unavailable"
        // instead of the generic "Failed to load CCL content".
        //
        // Brief 51 (22 Jun 2026) — Brief 49 widened the 500 detail
        // from a plain string to a structured object so we could see
        // exception_type/exception_message in the response. Rendering
        // that object directly inside JSX text or toast.error crashed
        // the page with React error #31 ("Objects are not valid as a
        // React child"). Coerce non-string details to a human-readable
        // string before they touch the DOM.
        const errDetail = errorMessage(err, "Failed to load CCL content");
        if (editor) {
          // Escape any HTML the message might contain before setContent.
          const safe = errDetail
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          editor.commands.setContent(`<p style="color:#b91c1c">${safe}</p>`);
        }
        toast.error(errDetail);
      } finally {
        setLoading(false);
      }
    };
    if (editor && submissionId) load();
  }, [editor, submissionId]);

  // ------------------------------------------------------------------
  // BM-H595: the automatic AI check against the FORF
  // ------------------------------------------------------------------
  // The server starts it when the draft is first built (and after a
  // Rebuild from FORF). While it runs the letter is locked; when it
  // finishes, the swept letter (if anything changed) replaces the editor
  // content, its suggestions go to the rail as advisory cards and the
  // change list appears with Undo.
  const handlePresweepFinished = useCallback((data) => {
    if (editor && data.html && (data.changes || []).length > 0) {
      editor.commands.setContent(data.html);
      setEditorHtml(data.html);
    }
    if (Array.isArray(data.findings)) setForfFindings(data.findings);
    // BM-H3747: placeable suggestions arrive as one-click replaces.
    const cards = presweepCards(data);
    if (cards.length) setComments((prev) => mergeComments(prev, cards));
    // BM-H3747 (5 Oct 2026): the first go now includes the full once-over,
    // run on the swept letter before the editor unlocks. Its cards replace
    // any older once-over cards; the sweep's own cards stay alongside.
    if (data.review_status === "done" && fetchLastReviewRef.current) {
      fetchLastReviewRef.current()
        .then((reviewCards) => {
          if (reviewCards) {
            setComments((prev) => mergeComments(
              prev.filter((c) => !String(c.id).startsWith("gap-")), reviewCards,
            ));
          }
        })
        .catch(() => {});
    }
  }, [editor]);
  const presweep = useCclPresweep({ submissionId, onFinished: handlePresweepFinished });
  seedPresweepRef.current = presweep.seed;
  const presweepRunning = presweep.running;

  useEffect(() => {
    if (editor) editor.setEditable(!reviewed && !presweepRunning);
  }, [editor, reviewed, presweepRunning]);

  const handleRebuildFromForf = useCallback(async () => {
    if (!editor) return;
    try {
      setRebuilding(true);
      const res = await axios.post(`${API}/submissions/${submissionId}/ccl-rebuild-from-forf`);
      const data = res.data || {};
      editor.commands.setContent(data.html || "");
      setEditorHtml(data.html || "");
      setForfChanged(false);
      setForfFindings(data.forf_findings || []);
      if (data.forf_facts) setForfFacts(data.forf_facts);
      setHasSaved(true);
      presweep.clearChanges();
      presweep.seed(data.presweep);
      toast.success("Rebuilt from the FORF. Your previous draft is in the version history.");
    } catch (err) {
      toast.error(errorMessage(err, "Rebuild from FORF failed"));
    } finally {
      setRebuilding(false);
    }
  }, [editor, submissionId, presweep]);

  // BM-H3747 (23 Oct 2026): the FORF address into the letter, nothing else
  // touched, then saved as a new version like any other save.
  const [updatingAddress, setUpdatingAddress] = useState(false);
  const handleUpdateAddress = useCallback(async () => {
    if (!editor) return;
    try {
      setUpdatingAddress(true);
      const res = await axios.post(`${API}/submissions/${submissionId}/ccl-update-address`, {
        html: editor.getHTML(),
      });
      editor.commands.setContent(res.data?.html || editor.getHTML());
      setEditorHtml(editor.getHTML());
      await handleSaveRef.current?.();
    } catch (err) {
      toast.error(errorMessage(err, "Could not update the address"));
    } finally {
      setUpdatingAddress(false);
    }
  }, [editor, submissionId]);

  const handleUndoPresweepChange = useCallback((change) => {
    const result = undoChange(editor, change);
    if (result.ok) {
      setEditorHtml(editor.getHTML());
      setHasSaved(false);
      presweep.removeChange(change.id);
    } else {
      toast.warning("That wording has been edited since the check, so it cannot be undone automatically.");
    }
  }, [editor, presweep]);

  const handleUndoAllPresweep = useCallback(async () => {
    if (!editor) return;
    try {
      setUndoingAll(true);
      const res = await axios.post(`${API}/submissions/${submissionId}/ccl-presweep/undo-all`);
      editor.commands.setContent(res.data?.html || "");
      setEditorHtml(res.data?.html || "");
      setHasSaved(true);
      presweep.clearChanges();
      toast.success("Back to the letter as built from the FORF");
    } catch (err) {
      toast.error(errorMessage(err, "Undo all failed"));
    } finally {
      setUndoingAll(false);
    }
  }, [editor, submissionId, presweep]);

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------
  const handleSave = useCallback(async () => {
    if (!editor) return;
    const html = editor.getHTML();
    try {
      setSaving(true);
      const res = await axios.post(`${API}/submissions/${submissionId}/save-ccl`, {
        html,
        saved_by: "fee_earner",
      });
      setHasSaved(true);
      setLastSavedAt(res.data.saved_at);
      // BM-H595: every save re-checks the letter against the FORF.
      if (Array.isArray(res.data?.forf_findings)) setForfFindings(res.data.forf_findings);
      setForfChanged(false);
      // BM-H21 (3 Jul 2026): every Save is a versioned checkpoint. Name the
      // checkpoint number in the toast so a fee earner in a long editing
      // session can see their saves are accumulating restorable history.
      const v = res.data?.version;
      toast.success(v ? `Checkpoint saved (v${v})` : "CCL saved");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save CCL"));
    } finally {
      setSaving(false);
    }
  }, [editor, submissionId]);
  handleSaveRef.current = handleSave;

  // ------------------------------------------------------------------
  // Confirm Review — BM-H11 (2 Jul 2026)
  // ------------------------------------------------------------------
  // Two problems addressed here:
  //
  // 1. AI Once-Over insertions + typed edits reset hasSaved (see onUpdate),
  //    but the backend reads ccl_html from the DB during structural
  //    validation. If the user clicks Confirm Review without saving,
  //    validation runs on stale HTML and returns 422 for sections the user
  //    just fixed. Guard: auto-save first when hasSaved is false, then
  //    proceed to confirm. If auto-save fails, bail — don't confirm.
  //
  // 2. The 422 detail includes an errors[] array listing which structural
  //    sections are missing, but the old handler only extracted .message.
  //    Users saw "required structural elements are missing" with no clue
  //    which ones. Surface the full list in a dedicated dialog.
  // BM-H597: after a successful revert the letter is a draft again.
  const handleReverted = useCallback(() => {
    setReviewed(false);
    setRevertedHere(true);
    setLiveSigningStatus(null);
    setHasSaved(true);
    editor?.setEditable(true);
    onReverted?.();
  }, [editor, onReverted]);

  const handleConfirmReview = useCallback(async () => {
    if (!editor) return;
    try {
      setConfirming(true);
      // Auto-save any pending edits before validating server-side.
      if (!hasSaved) {
        const html = editor.getHTML();
        try {
          const saveRes = await axios.post(`${API}/submissions/${submissionId}/save-ccl`, {
            html,
            saved_by: "fee_earner",
          });
          setHasSaved(true);
          setLastSavedAt(saveRes.data.saved_at);
        } catch (saveErr) {
          toast.error(
            errorMessage(saveErr, "Failed to save before confirming — try Save CCL manually"),
          );
          return;
        }
      }
      await axios.post(`${API}/submissions/${submissionId}/confirm-review`);
      setReviewed(true);
      editor.setEditable(false);
      toast.success("CCL review confirmed");
      onReviewConfirmed?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured 422 from the structural validator — pop the dialog
      // so the user can see WHICH sections need fixing.
      if (
        detail &&
        typeof detail === "object" &&
        (detail.code === "ccl_structural_validation_failed" ||
          detail.code === "ccl_forf_inconsistent") &&
        Array.isArray(detail.errors)
      ) {
        setStructuralError({
          summary: detail.message || "CCL cannot be confirmed.",
          errors: detail.code === "ccl_forf_inconsistent"
            ? detail.errors.map((e) => ({ ...e, section: e.section || "Does not match the FORF" }))
            : detail.errors,
          matterType: detail.matter_type || "",
          matterReference: detail.matter_reference || "",
        });
      } else {
        toast.error(errorMessage(err, "Failed to confirm review"));
      }
    } finally {
      setConfirming(false);
    }
  }, [submissionId, editor, onReviewConfirmed, hasSaved]);

  // ------------------------------------------------------------------
  // Export as Word
  // ------------------------------------------------------------------
  const handleExport = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/submissions/${submissionId}/generate-letter`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `CCL_${submissionId}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const detail = await parseBlobError(err);
      toast.error(`Failed to export: ${detail}`);
    }
  }, [submissionId]);

  // ------------------------------------------------------------------
  // Insert AI-drafted text into TipTap
  // ------------------------------------------------------------------
  // Strip tags and normalise whitespace/case so drafted HTML can be compared
  // against plain document text regardless of formatting differences.
  const normaliseForCompare = useCallback((s) => {
    if (!s) return "";
    return s
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }, []);

  // BM-H528 (15 Sep 2026) duplicate guard, kept for BM-H596 insert_after:
  // the target block's own text plus the block after it is the only window a
  // duplicate could land in.
  const getSurroundingText = useCallback((doc, suggestion) => {
    const blocks = blocksFromDoc(doc);
    const block = findSuggestionBlock(blocks, suggestion);
    if (!block) return "";
    const next = blocks[block.blockId + 1];
    return [block.text, next ? next.text : ""].join(" ");
  }, []);

  // Is the normalised drafted text already substantially present in the
  // window immediately around the insert point? A straightforward
  // containment/equality check on normalised (whitespace/case-insensitive)
  // text is sufficient here — see BM-H524-decisions.md for why a fuzzy-diff
  // algorithm would be over-engineering this fix.
  const isDuplicateInsert = useCallback((draftedHtml, surroundingText) => {
    const draftedPlain = normaliseForCompare(draftedHtml);
    const windowPlain = normaliseForCompare(surroundingText);
    if (!draftedPlain || !windowPlain) return false;
    // Ignore trivially short drafts (e.g. a single word) — containment on
    // very short strings produces false positives too easily and isn't the
    // failure mode we're guarding against (verbatim boilerplate paragraphs).
    if (draftedPlain.length < 20) return false;
    return windowPlain.includes(draftedPlain) || draftedPlain === windowPlain;
  }, [normaliseForCompare]);

  // BM-H596 (23 Sep 2026): apply one suggestion exactly where the check put
  // it, or not at all. BM-H120 anchored on a quote and fell back to the end
  // of the letter when the quote was not found, and it could only ever insert
  // after the anchor, so a Re line fix became a second Re line. Now:
  //   replace      -> the target words are replaced in place in that block
  //   insert_after -> a new paragraph straight after that block
  //   advisory     -> never applied (the card has no Apply button)
  // If the block has changed since the check, nothing happens and the card
  // says so. There is no end-of-letter fallback.
  const handleApplySuggestion = useCallback((comment) => {
    if (!editor) return { ok: false, reason: "no_editor" };
    const suggestion = { ...comment, text: comment.suggestedText };
    if (suggestion.op === "insert_after") {
      const html = markdownToHtml(suggestion.text || "");
      if (isDuplicateInsert(html, getSurroundingText(editor.state.doc, suggestion))) {
        toast.warning("This looks like it's already in the letter, so it was skipped to avoid duplicating it. Please check the section manually.");
        return { ok: false, reason: "duplicate" };
      }
    }
    const result = applySuggestion(editor, suggestion, markdownToHtml);
    if (result.ok) {
      setEditorHtml(editor.getHTML());
      // BM-H11: an applied fix invalidates the saved snapshot.
      setHasSaved(false);
    }
    return result;
  }, [editor, getSurroundingText, isDuplicateInsert]);

  // ── Brief 42b — rail interaction handlers ──────────────────────────
  // BM-H17 (2 Jul 2026) — mirror Accept / Reject / Undo to the server so
  // the decision survives a page reload. Fire-and-forget POST: React
  // state updates immediately for a snappy UI; a failed persist logs but
  // does not roll back, because the next Save (or the next sweep) will
  // reconcile. Vanessa's Clare Drury CCL taught us the reverse (silent
  // loss on reload) is far worse than a rare cosmetic desync.
  const persistGapStatus = useCallback(async (gapId, status) => {
    if (!submissionId || !gapId) return;
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      await axios.post(
        `${API}/hub/ccl-review/${submissionId}/gap-status`,
        { gap_id: gapId, status },
        { headers: { "X-Bonsai-User-Id": userId } },
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("BM-H17: gap-status persist failed", err);
    }
  }, [submissionId]);

  const handleCommentAccept = useCallback((id) => {
    const comment = comments.find((c) => c.id === id);
    if (!comment) return;
    if (comment.suggestedText || comment.op === "remove") {
      const result = handleApplySuggestion(comment);
      if (!result.ok) {
        if (result.reason === "stale" || result.reason === "advisory") {
          setComments((prev) => prev.map((c) => (c.id === id ? { ...c, stale: true } : c)));
          toast.warning(STALE_MESSAGE);
        }
        return;
      }
    }
    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, status: "accepted" } : c)));
    persistGapStatus(id, "accepted");
  }, [comments, handleApplySuggestion, persistGapStatus]);

  const handleCommentReject = useCallback((id) => {
    setComments((prev) => prev.map((c) =>
      c.id === id ? { ...c, status: "rejected" } : c,
    ));
    persistGapStatus(id, "rejected");
  }, [persistGapStatus]);

  // BM-H17 — Undo returns a card to the open state on both the rail and
  // the server. Fee earners can toggle a decision if they change their
  // mind or mis-click.
  const handleCommentUndo = useCallback((id) => {
    setComments((prev) => prev.map((c) =>
      c.id === id ? { ...c, status: "open" } : c,
    ));
    persistGapStatus(id, "open");
  }, [persistGapStatus]);

  // Scroll the document so the block anchored to this comment is visible,
  // and mark it active so the connecting spine + card highlight light up.
  const handleFocusAnchor = useCallback((anchorId) => {
    setActiveAnchorId(anchorId);
    const container = a4ScrollRef.current;
    if (!container || !anchorId) return;
    const target = container.querySelector(`[data-anchor-id="${cssEscape(anchorId)}"]`);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("ccl-anchor-flash");
      setTimeout(() => target.classList.remove("ccl-anchor-flash"), 900);
    }
  }, []);

  // Clicking a tagged paragraph focuses its comment in the rail.
  const handleA4Click = useCallback((e) => {
    const block = e.target.closest?.("[data-anchor-id]");
    if (block) setActiveAnchorId(block.getAttribute("data-anchor-id"));
  }, []);

  // ------------------------------------------------------------------
  // Derived letterhead values
  // ------------------------------------------------------------------
  const resolvedClientName = clientName || submissionData?.data?.Q1 || "";

  // ── Brief 69.1 — AI Once-Over (rail-driven) ────────────────────────
  // The rail owns the trigger; the hook owns the fetch + transform and
  // writes gap suggestions straight into the `comments` model.
  const getCclText = useCallback(() => editorHtml, [editorHtml]);
  // BM-H595: the once-over is given the FORF facts (fee from Q14, the
  // disbursements, payment on account, scope) rather than the old Q10 field.
  const sweepSubmissionData = useMemo(
    () => (forfFacts ? { ...(submissionData || {}), forf_facts: forfFacts } : submissionData),
    [submissionData, forfFacts],
  );
  const { runSweep, sweeping, draftSection, refineSection, fetchLastReview, notice: replyNotice } = useCclSweep({
    submissionId,
    getCclText,
    matterType: matterType || submissionData?.data?.Q9 || "",
    feeQuoted: feeQuoted || forfFacts?.fee_amount || "",
    clientName: resolvedClientName,
    submissionData: sweepSubmissionData,
    setComments,
  });
  fetchLastReviewRef.current = fetchLastReview;

  // Draft replacement copy for a single gap, then stash it on the comment so
  // the rail can preview it and Accept can insert it.
  const handleCommentDraft = useCallback(async (comment) => {
    setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, drafting: true } : c)));
    try {
      const drafted = await draftSection(comment);
      setComments((prev) =>
        prev.map((c) =>
          c.id === comment.id
            ? { ...c, ...placeFrom(drafted, c), drafting: false, suggestedText: drafted.text }
            : c
        )
      );
    } catch {
      toast.error("Draft generation failed");
      setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, drafting: false } : c)));
    }
  }, [draftSection]);

  // BM-H121 — the Reply box lets a fee earner type reasoning against a
  // drafted suggestion ("not quite right", "needs to mention the
  // guarantor too") and have the AI actually redraft using that feedback,
  // rather than just noting it and moving on. If no draft exists yet on
  // the comment, treat the reply as the drafting instruction itself —
  // refineSection handles an empty previous_draft fine on the backend.
  //
  // BM-H125 (29 Jul 2026): this callback used to live before the
  // useCclSweep destructure that declares refineSection. Its deps
  // array `[refineSection]` therefore ran through the Temporal Dead
  // Zone on first render and crashed every draft-CCL page with
  // "Cannot access 'refineSection' before initialization" — minified
  // to "Cannot access 'se' before initialization" in prod. Moved to
  // sit after handleCommentDraft (same shape, already correctly
  // ordered relative to the destructure) so the dep is always live.
  const handleCommentReply = useCallback(async (id, text) => {
    if (!text || !text.trim()) return;
    let target = null;
    setComments((prev) => prev.map((c) => {
      if (c.id === id) {
        target = c;
        return { ...c, drafting: true };
      }
      return c;
    }));
    if (!target) return;
    try {
      const refined = await refineSection(target, text.trim());
      // BM-H3747 (19 Oct 2026): say what the reply did. It redrafts the card;
      // nothing goes into the letter until Insert, and the reply is kept for
      // the next once-over.
      const noChange = refined.op === "none";
      const placed = !noChange && refined.op !== "advisory";
      const kept = Boolean(refined.reply?.kept);
      setComments((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                ...(noChange ? { op: "advisory" } : placeFrom(refined, c)),
                advisoryReason: placed ? null : refined.advisoryReason || c.advisoryReason,
                drafting: false,
                suggestedText: placed ? refined.text : null,
                replyWording: placed || noChange ? null : refined.text,
                reply: { feedback: text.trim(), at: new Date().toISOString(), kept, op: refined.op, note: refined.note || "" },
              }
            : c
        )
      );
      toast.success(noChange
        ? "Noted: no change to the letter for this point."
        : placed
          ? "Redrafted from your reply. Press Insert into letter to put it in."
          : "Redrafted from your reply, but the AI could not find where it goes in the letter.");
    } catch {
      toast.error("Redraft failed. The previous suggestion is unchanged.");
      setComments((prev) => prev.map((c) => (c.id === id ? { ...c, drafting: false } : c)));
    }
  }, [refineSection]);

  // BM-H121 — hand-edit a drafted suggestion in the rail before Insert.
  // AI drafts aren't always right first time; this lets the fee earner
  // fix wording directly instead of Discard + redraft.
  const handleCommentEditDraft = useCallback((id, newText) => {
    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, suggestedText: newText } : c)));
  }, []);

  const handleCommentDiscard = useCallback((id) => {
    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, suggestedText: null } : c)));
  }, []);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading CCL...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ccl-editor-shell">
      {folded ? (
        <CclSentSummary
          clientSigned={cclClientSigned || Boolean(offSystem?.client_accepted)}
          offSystem={!cclSent}
          onShow={() => setLetterOpen(true)}
        />
      ) : (
      <>
      {sentAnyWay && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setLetterOpen(false)} data-testid="ccl-hide-letter">
            Hide letter
          </Button>
        </div>
      )}
      {/* ----------------------------------------------------------------
          Brief 42b — two-column layout: document column (fixed comfortable
          width, never obscured) + docked comments rail. Stacks to a single
          column at < lg; on tablet/mobile the rail drops below the document
          (a bottom drawer in effect).
      ---------------------------------------------------------------- */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">

      {/* === Document column ============================================ */}
      <div className="min-w-0 flex-1">
      {/* ----------------------------------------------------------------
          Outer card — relative so sticky toolbar anchors inside it
      ---------------------------------------------------------------- */}
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white relative" data-testid="ccl-editor">

        {/* ---- Sticky toolbar ---- */}
        <div
          className="sticky top-0 z-10 flex items-center gap-1 px-3 py-2 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm flex-wrap"
          data-testid="ccl-toolbar"
        >
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleBold().run()}
            active={editor?.isActive("bold")}
            disabled={reviewed}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            active={editor?.isActive("italic")}
            disabled={reviewed}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-slate-200 mx-1" />
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor?.isActive("heading", { level: 2 })}
            disabled={reviewed}
            title="Heading"
          >
            <Heading2 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor?.isActive("heading", { level: 3 })}
            disabled={reviewed}
            title="Subheading"
          >
            <Heading3 className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-slate-200 mx-1" />
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            active={editor?.isActive("bulletList")}
            disabled={reviewed}
            title="Bullet list"
          >
            <List className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            active={editor?.isActive("orderedList")}
            disabled={reviewed}
            title="Numbered list"
          >
            <ListOrdered className="w-4 h-4" />
          </ToolbarButton>
          {/* BM-H4517 (19 Oct 2026, Vanessa: "ensure that non-claude users have
              full editing in the CCL wizard"): the action points and fees
              tables had no way to add or take out a row by hand. */}
          {inTable && !reviewed && (
            <>
              <div className="w-px h-5 bg-slate-200 mx-1" />
              <span data-testid="ccl-table-tools" className="flex items-center gap-0.5">
                <ToolbarButton
                  onClick={() => editor?.chain().focus().addRowBefore().run()}
                  title="Add a row above"
                >
                  <BetweenHorizontalStart className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().addRowAfter().run()}
                  title="Add a row below"
                >
                  <BetweenHorizontalEnd className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().deleteRow().run()}
                  title="Delete this row"
                >
                  <Trash2 className="w-4 h-4" />
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => editor?.chain().focus().deleteTable().run()}
                  title="Delete the whole table"
                >
                  <Table2 className="w-4 h-4" />
                </ToolbarButton>
              </span>
            </>
          )}
          <div className="w-px h-5 bg-slate-200 mx-1" />
          <ToolbarButton
            onClick={() => editor?.chain().focus().undo().run()}
            disabled={reviewed || !editor?.can().undo()}
            title="Undo"
          >
            <Undo2 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().redo().run()}
            disabled={reviewed || !editor?.can().redo()}
            title="Redo"
          >
            <Redo2 className="w-4 h-4" />
          </ToolbarButton>
          <div className="w-px h-5 bg-slate-200 mx-1" />
          <ToolbarButton
            onClick={() => setPreviewing((p) => !p)}
            active={previewing}
            title={previewing ? "Back to Edit" : "Preview"}
          >
            {previewing ? <PenLine className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </ToolbarButton>

          <div className="flex-1" />

          {/* Last saved timestamp */}
          {lastSavedAt && (
            <span className="text-xs text-slate-400 mr-2">
              Saved {new Date(lastSavedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          {source === "template" && !hasSaved && (
            <span className="text-xs text-status-amber mr-2">From template — edit and save</span>
          )}
          {/* BM-H11: warn when the editor has unsaved changes so lawyers
              see confirm-review will auto-save before validating. */}
          {!hasSaved && source !== "template" && lastSavedAt && (
            <span className="text-xs text-status-amber mr-2" data-testid="ccl-unsaved-badge">
              Unsaved changes
            </span>
          )}
        </div>

        <CclManualUpload
          matterRef={submissionData?.matter_reference || submissionData?.matter_ref || ""}
        />

        {/* BM-H160 third option: the CCL and welcome pack went out off CaseMinder. */}
        <CclSentOffSystem
          matterRef={submissionData?.matter_reference || submissionData?.matter_ref || ""}
          onState={setOffSystem}
        />

        {/* BM-H595: the draft versus the FORF. */}
        {!reviewed && (
          <CclForfCheck
            running={presweepRunning}
            status={presweep.status}
            reason={presweep.reason}
            changes={presweep.changes}
            findings={forfFindings}
            forfChanged={forfChanged}
            onRebuild={handleRebuildFromForf}
            rebuilding={rebuilding}
            onUndoChange={handleUndoPresweepChange}
            onUndoAll={handleUndoAllPresweep}
            undoingAll={undoingAll}
            onUpdateAddress={handleUpdateAddress}
            updatingAddress={updatingAddress}
            disabled={presweepRunning}
          />
        )}

        {/* ---- A4 scroll pane ---- */}
        <div
          ref={a4ScrollRef}
          onClick={handleA4Click}
          className="overflow-y-auto bg-[#f5f5f2]"
          style={{ maxHeight: "560px" }}
          data-testid="ccl-a4-scroll"
        >
          {/* Brief 63 (23 Jun 2026): editor surface must visually match the
              Calibri 11 .docx the client receives. White page, dark text,
              Calibri (with safe web fallbacks since users may not have Calibri
              installed locally — Carlito is the free GNU clone and renders
              identically to Word for our purposes). */}
          <style>{`
            .ccl-editor-page p { margin: 0 0 8px 0; }
            .ccl-editor-page h1, .ccl-editor-page h2, .ccl-editor-page h3 {
              font-weight: 600;
              font-size: 14.7px;
              margin: 12px 0 4px 0;
            }
            .ccl-editor-page ul, .ccl-editor-page ol {
              margin: 4px 0 8px 24px;
              padding-left: 0;
            }
            .ccl-editor-page li { margin-bottom: 4px; }
          `}</style>
          <div
            className="ccl-editor-page mx-auto my-6 bg-white text-slate-900 px-12 py-10 shadow-md rounded-sm"
            style={{
              width: "794px",
              minHeight: "1123px",
              fontFamily: '"Calibri", "Carlito", "Segoe UI", system-ui, sans-serif',
              fontSize: '14.7px',
              lineHeight: 1.4,
              color: '#1f2937',
            }}
            data-testid="ccl-a4-page"
          >
            {/* Editor body / preview */}
            {previewing ? (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: editor?.getHTML() || "" }}
                data-testid="ccl-preview"
              />
            ) : (
              <div className={reviewed ? "opacity-75" : ""}>
                <EditorContent editor={editor} data-testid="ccl-editor-content" />
              </div>
            )}
          </div>
        </div>

        {/* BM-H3849: a director-marked UAT matter lets the whole team take every step. */}
        {submissionId && (
          <UatMatterToggle
            submissionId={submissionId}
            uatMatter={!!submissionData?.uat_matter}
            markedBy={submissionData?.uat_marked_by || ""}
          />
        )}

        {/* ---- Step strip (BM-H18) ----
            Tells the fee earner exactly where they are in the CCL flow and
            what the next button click does. Current step highlighted in teal. */}
        {!reviewed && (
          <div
            className="flex items-center gap-2 px-4 py-2 border-t border-slate-200 bg-white text-xs"
            data-testid="ccl-step-strip"
          >
            <span className="font-semibold text-teal">1. Review CCL</span>
            <span className="text-slate-300">→</span>
            <span className="font-semibold text-teal">2. Save &amp; Confirm Review</span>
            <span className="text-slate-300">→</span>
            <span className="text-slate-400">3. Send Client Care Pack (on matter page)</span>
          </div>
        )}
        {reviewed && (
          <div
            className="flex items-center gap-2 px-4 py-2 border-t border-slate-200 bg-emerald-50 text-xs"
            data-testid="ccl-step-strip"
          >
            <span className="text-emerald-700">✓ Reviewed — return to the matter page to Send Client Care Pack</span>
          </div>
        )}

        {/* ---- Action bar ---- */}
        <div
          className="flex items-center gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50/50"
          data-testid="ccl-actions"
        >
          {!reviewed ? (
            <>
              {/* BM-H18 (2 Jul 2026) / BM-H21 (3 Jul 2026): consolidated CCL flow.
                  The old three-button row (Save CCL / Create Draft / Confirm Review)
                  gave zero signal about order, and Save CCL didn't tell the user they
                  still had to hit Confirm Review before the client care pack send button
                  ungated. Vanessa lost time to this repeatedly.

                  Flow now:
                    1. "Save & Confirm Review" — the primary green button. Auto-saves,
                       runs structural validation, and flips docxReviewed=true so
                       the parent detail page's Send Client Care Pack button ungates.
                    2. "Save" — working save while editing. BM-H21: every Save is now a
                       versioned checkpoint (backend appends to ccl_versions and returns
                       the version number, surfaced in the toast). Confirm Review already
                       auto-saves (BM-H11) so Save is optional but useful as an explicit
                       checkpoint during long editing sessions.

                  BM-H21 removed the third "Create Draft" ghost button entirely — it
                  duplicated persistence, confused the flow, and its email-to-clientcare
                  behaviour belongs to the Send Client Care Pack surface, not here. */}
              <Button
                onClick={handleConfirmReview}
                disabled={confirming || saving || presweepRunning}
                size="sm"
                className="bg-emerald-700 hover:bg-emerald-600 text-white gap-2"
                data-testid="confirm-review-btn"
                title="Save the CCL, run structural checks, and mark it reviewed so you can send the client care pack"
              >
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Save &amp; Confirm Review
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || confirming || presweepRunning}
                size="sm"
                variant="outline"
                className="gap-2 border-teal/60 text-teal hover:bg-teal/5"
                data-testid="save-ccl-btn"
                title="Save your edits so far without confirming review"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </Button>
            </>
          ) : (
            // Brief 70: post-review signing pipeline (Apply Sig / Send / status).
            <>
              <CclPostReviewActions
                submissionId={submissionId}
                submissionData={submissionData}
                matterRef={submissionData?.matter_reference || ""}
                clientName={clientName}
                initialSigningStatus={revertedHere ? null : signingStatus}
                initialSigningToken={revertedHere ? null : signingToken}
                onSigningStatusChange={setLiveSigningStatus}
              />
              {/* BM-H597: Revert to draft, until any client signs. */}
              {!cclClientSigned && (
                <CclRevertToDraft
                  submissionId={submissionId}
                  sent={cclSent}
                  clientSigned={cclClientSigned}
                  onReverted={handleReverted}
                />
              )}
            </>
          )}

          <div className="flex-1" />

          <button
            onClick={handleExport}
            className="text-xs text-slate-500 hover:text-slate-700 underline flex items-center gap-1"
            data-testid="export-ccl-btn"
          >
            <FileDown className="w-3 h-3" /> Export as Word
          </button>
        </div>
      </div>

      </div>
      {/* === End document column ======================================== */}

      {/* === Comments rail ============================================= */}
      <CclCommentsRail
        comments={comments}
        collapsed={railCollapsed}
        onToggleCollapse={() => setRailCollapsed((v) => !v)}
        onAccept={handleCommentAccept}
        onReject={handleCommentReject}
        onUndo={handleCommentUndo}
        onReply={handleCommentReply}
        onDraft={handleCommentDraft}
        onEditDraft={handleCommentEditDraft}
        onDiscard={handleCommentDiscard}
        onFocusAnchor={handleFocusAnchor}
        onRunSweep={runSweep}
        sweeping={sweeping}
        activeAnchorId={activeAnchorId}
        authorLabel="AI Once-Over"
        notice={replyNotice}
      />
      {/* === End comments rail ========================================= */}

      </div>
      </>
      )}

      {/* ------------------------------------------------------------------
          BM-H11 (2 Jul 2026) — Structural validation dialog
          ------------------------------------------------------------------
          The 422 from POST /confirm-review carries a structured errors[]
          array from ccl_structural_validator. The old toast was too small
          to list each missing element; lawyers were left with "required
          structural elements are missing" and no clue which sections to
          fix. This dialog surfaces every {code, section, message} entry.
      ------------------------------------------------------------------ */}
      <Dialog
        open={!!structuralError}
        onOpenChange={(open) => { if (!open) setStructuralError(null); }}
      >
        <DialogContent className="max-w-lg" data-testid="ccl-structural-error-dialog">
          <DialogHeader>
            <DialogTitle>CCL cannot be confirmed</DialogTitle>
            <DialogDescription>
              {structuralError?.summary ||
                "Required structural elements are missing. Fix them and confirm again."}
            </DialogDescription>
          </DialogHeader>
          {structuralError?.matterReference && (
            <p className="text-xs text-slate-500 -mt-2">
              Matter {structuralError.matterReference}
              {structuralError.matterType ? ` · ${structuralError.matterType}` : ""}
            </p>
          )}
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700 max-h-72 overflow-y-auto">
            {(structuralError?.errors || []).map((e, idx) => (
              <li key={`${e.code || "err"}-${idx}`} data-testid="ccl-structural-error-item">
                <span className="font-medium">
                  {e.section ? e.section : (e.code || "Structural issue")}
                </span>
                {e.message ? <span className="text-slate-600"> — {e.message}</span> : null}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStructuralError(null)}
              data-testid="ccl-structural-error-dismiss"
            >
              Back to editor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
